import { useState } from "react";
import {
  Button,
  Checkbox,
  Link,
  TextField,
  Typography,
  InputAdornment,
  Box,
  IconButton,
  useTheme,
  CircularProgress,
  List,
  ListItem,
  Popover,
} from "@mui/material";
import Spark_Logo from "../../../assets/baseVersion/Spark_Newlogo.svg";
import { useFormik } from "formik";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import "./register.scss";
import axiosInstance from "../../../axios/axios";
import { toast } from "react-toastify";
import { useEncryptService } from "../../../shared/hooks";
import PrivacyPolicy from "./PrivacyPolicy";
import TermsConditions from "./TermsConditions";
import DoneIcon from "@mui/icons-material/Done";
import { END_POINTS } from "../../../shared";
import React from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import * as yup from "yup";
import { CONSTANT } from "../../../shared/constants/constant-data";

const commonEmailDomains = [
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
];

const isBusinessEmail = (email: string) => {
  const domain = email.split("@")[1];
  return !commonEmailDomains.includes(domain);
};

const loginValidationSchema = yup.object().shape({
  firstname: yup
    .string()
    .required(CONSTANT.AUTHENTICATION.AUTH_FIRST_NAME_MSG)
    .min(1, CONSTANT.AUTHENTICATION.AUTH_FIRST_NAME_MIN_LENGTH)
    .max(31, CONSTANT.AUTHENTICATION.AUTH_FIRST_NAME_MAX_LENGTH)
    .matches(
      /^[a-zA-Z]+( [a-zA-Z]+)*$/,
      CONSTANT.AUTHENTICATION.AUTH_FIRST_NAME_PATTERN
    ),

  lastname: yup
    .string()
    .required(CONSTANT.AUTHENTICATION.AUTH_LAST_NAME_MSG)
    .min(1, CONSTANT.AUTHENTICATION.AUTH_LAST_NAME_MIN_LENGTH)
    .max(31, CONSTANT.AUTHENTICATION.AUTH_LAST_NAME_MAX_LENGTH)
    .matches(
      /^[a-zA-Z]+( [a-zA-Z]+)*$/,
      CONSTANT.AUTHENTICATION.AUTH_LAST_NAME_PATTERN
    ),

  organisationname: yup
    .string()
    .required(CONSTANT.AUTHENTICATION.AUTH_ORG_NAME_REQUIRED)
    .min(6, CONSTANT.AUTHENTICATION.AUTH_ORG_NAME_MIN_LENGTH)
    .max(64, CONSTANT.AUTHENTICATION.AUTH_ORG_NAME_MAX_LENGTH)
    .matches(/^\S+(\s\S+)*$/, CONSTANT.AUTHENTICATION.AUTH_ORG_NAME_PATTERN),

  username: yup
    .string()
    .required(CONSTANT.AUTHENTICATION.AUTH_EMAIL_REQUIRED)
    .email(CONSTANT.AUTHENTICATION.USER_NAME_EMAIL)
    .matches(
      CONSTANT.AUTHENTICATION.AUTH_EMAIL_PATTERN,
      CONSTANT.AUTHENTICATION.USER_NAME_EMAIL
    )
    .max(64, CONSTANT.AUTHENTICATION.USER_NAME_MAX_LENGTH)
    .test(
      "is-business-email",
      "Please use business email",
      (value: string | undefined) => isBusinessEmail(value || "")
    ),

  password: yup
    .string()
    .required(CONSTANT.AUTHENTICATION.SIGN_IN_PASSWORD_REQUIRED)
    .min(12, CONSTANT.AUTHENTICATION.AUTH_PASSWORD_MIN_LENGTH)
    .max(20, CONSTANT.AUTHENTICATION.AUTH_PASSWORD_MAX_LENGTH)
    .matches(/^\S*$/, CONSTANT.AUTHENTICATION.AUTH_PASSWORD_NO_SPACE)
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^_\-+=(){}\[\].,:~`|\\;"'<>/])[A-Za-z\d@$!%*?&#^_\-+=(){}\[\].,:~`|\\;"'<>/]{12,20}$/,
      CONSTANT.AUTHENTICATION.AUTH_PASSWORD_FORMAT
    ),
  rememberMe: yup.boolean(),

  recaptcha: yup.string().nullable(),

  offLineCaptcha: yup.string().nullable(),
});

const Register = () => {
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [isPrivacyPolicyModalOpen, setIsPrivacyPolicyModalOpen] =
    useState(false);
  const [isTermsAndConditionsModalOpen, setIsTermsAndConditionsModalOpen] =
    useState(false);
  const [isRegisterSuccess, setIsRegisterSuccess] = useState(false);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = React.useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { doEncryptLogin } = useEncryptService();
  const [loading, setLoading] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const open = Boolean(anchorEl);
  const id = open ? "password-popover" : undefined;

  const handleClose = () => {
    setAnchorEl(null);
  };

  const conditions = [
    { label: "1 Uppercase Letter", regex: /[A-Z]/ },
    { label: "1 Lowercase Letter", regex: /[a-z]/ },
    { label: "1 Number", regex: /[0-9]/ },
    {
      label: "1 Special Character",
      regex: /[@$!%*?&#^_\-+=(){}\[\].,:~`|\\;"'<>/]/,
    },
    { label: "Minimum 12 Characters required", regex: /^.{12,}$/ },
    {
      label: "Maximum 20 Characters allowed",
      validate: (password: string) => password.length <= 20,
    },
  ];

  const lastErrorMessageRef = React.useRef<string | null>(null);
  const toastTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const toastDuration = 5000;

  const handleNameInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    fieldName: string
  ) => {
    const value = e.target.value;
    if (value.length <= 32) {
      formik.handleChange(e);
    }
  };

  const getFieldError = (fieldName: string, value: string) => {
    if (value.length >= 32) {
      return "Maximum 32 characters allowed";
    }
    return formik.errors[fieldName as keyof typeof formik.errors];
  };

  const formik = useFormik({
    initialValues: {
      username: "",
      password: "",
      firstname: "",
      lastname: "",
      organisationname: "",
    },
    validationSchema: loginValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (!isButtonDisabled) {
        try {
          setLoading(true);
          const response = await axiosInstance.post(END_POINTS.REGISTER, {
            firstname: values.firstname,
            lastname: values.lastname,
            organization_name: values.organisationname,
            password: doEncryptLogin(values.password),
            organization_email: values.username.toLowerCase(),
          });

          // toast(response.data.message, { type: "success" });
          toast.success(response?.data?.message || "Registration successful");
          setIsRegisterSuccess(true);
        } catch (error: any) {
          console.error("Error changing password:", error);

          const errorMessage =
            error?.response?.data?.message || "Something went wrong";

          // Only show toast if error message is different or previous toast duration has ended
          if (
            lastErrorMessageRef.current !== errorMessage ||
            !toastTimeoutRef.current
          ) {
            toast(errorMessage, { type: "error" });
            lastErrorMessageRef.current = errorMessage;
            if (toastTimeoutRef.current) {
              clearTimeout(toastTimeoutRef.current);
            }
            toastTimeoutRef.current = setTimeout(() => {
              toastTimeoutRef.current = null;
              lastErrorMessageRef.current = null;
            }, toastDuration);
          }
          setIsButtonDisabled(false);
        } finally {
          setIsButtonDisabled(false);
          setLoading(false);
        }
      }
    },
  });
  // Autofill validation for username
  React.useEffect(() => {
    // For username/email
    if (
      formik.values.username &&
      !formik.touched.username &&
      formik.errors.username
    ) {
      formik.setFieldTouched("username", true, false);
      formik.validateField("username");
    }
    // Repeat for other fields as needed
  }, [formik.values.username, formik.errors.username]);

  return (
    <>
      {isRegisterSuccess ? (
        <div className="success-container">
          <DoneIcon className="success-icon" />
          <Typography className="success-title">
            Your account has been created!
          </Typography>
          <Typography className="success-subtitle">
            We've sent an email to verify your account
          </Typography>
          <Typography className="success-email">
            {formik.values.username}
          </Typography>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            className="btn btn-primary"
            href="/login"
          >
            Sign In
          </Button>
        </div>
      ) : (
        <div
          className="login-container"
          style={{ height: "auto", overflow: "visible" }}
        >
          <div>
            <img src={Spark_Logo} alt="Login Logo" style={{ height: "35px" }} />
          </div>
          <h5 className="mb_2">Create new account</h5>
          <p className="login-subtitle">
            Sign up with your details to start using Spark
          </p>
          <form
            onSubmit={formik.handleSubmit}
            autoComplete="off"
            spellCheck={false}
          >
            <div>
              {/* Form Fields Section - no internal scroll, grows with content */}
              <Box
                className="scroll"
                sx={{
                  overflowY: "visible", // No scrollbar - let container handle overflow
                  overflowX: "hidden",
                  marginBottom: "16px",
                }}
              >
                <Box display="flex" gap={2} className="mb_3">
                  <TextField
                    sx={{ mt: 2 }}
                    id="firstname"
                    label="First Name"
                    variant="outlined"
                    required
                    fullWidth
                    name="firstname"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.firstname}
                    error={
                      (formik.touched.firstname &&
                        Boolean(formik.errors.firstname)) ||
                      formik.values.firstname.length >= 33
                    }
                    helperText={
                      formik.touched.firstname && formik.errors.firstname
                    }
                    inputProps={{
                      maxLength: 32,
                    }}
                  />
                  <TextField
                    sx={{ mt: 2 }}
                    id="lastname"
                    label="Last Name"
                    variant="outlined"
                    required
                    fullWidth
                    name="lastname"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.lastname}
                    error={
                      (formik.touched.lastname &&
                        Boolean(formik.errors.lastname)) ||
                      formik.values.lastname.length >= 33
                    }
                    helperText={
                      formik.touched.lastname && formik.errors.lastname
                    }
                    inputProps={{
                      maxLength: 32,
                    }}
                  />
                </Box>
                <TextField
                  id="username"
                  label="Email"
                  variant="outlined"
                  required
                  fullWidth
                  name="username"
                  autoComplete="off"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.username}
                  error={
                    formik.touched.username && Boolean(formik.errors.username)
                  }
                  helperText={formik.touched.username && formik.errors.username}
                  className="mb_3"
                  inputProps={{
                    autoComplete: "off",
                    autoCorrect: "off",
                    autoCapitalize: "none",
                    "data-lpignore": "true",
                    "data-1p-ignore": "true",
                  }}
                />
                <TextField
                  id="organisationname"
                  label="Organization"
                  variant="outlined"
                  required
                  fullWidth
                  name="organisationname"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.organisationname}
                  error={
                    formik.touched.organisationname &&
                    Boolean(formik.errors.organisationname)
                  }
                  helperText={
                    formik.touched.organisationname &&
                    formik.errors.organisationname
                  }
                  className="mb_3"
                />
                <TextField
                  id="password"
                  label="Password"
                  variant="outlined"
                  fullWidth
                  required
                  type={showPassword ? "text" : "password"}
                  name="password"
                  autoComplete="new-password"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                  error={
                    formik.touched.password && Boolean(formik.errors.password)
                  }
                  helperText={formik.touched.password && formik.errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  className="mb_3"
                  inputProps={{
                    autoComplete: "new-password",
                    autoCorrect: "off",
                    autoCapitalize: "none",
                    "data-lpignore": "true",
                    "data-1p-ignore": "true",
                  }}
                />
              </Box>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <IconButton
                onClick={handleClick}
                aria-describedby={id}
                sx={{ "&:focus": { outline: "none" } }}
              >
                <InfoIcon style={{ color: "#868fa5" }} />
              </IconButton>
              <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
              >
                <Typography sx={{ p: 2, maxWidth: 350 }}>
                  <List dense>
                    {conditions.map((condition, index) => {
                      const isMatched = condition.regex
                        ? condition.regex.test(formik.values.password)
                        : condition.validate
                          ? condition.validate(formik.values.password)
                          : false;
                      return (
                        <ListItem key={index} sx={{ display: "flex", gap: 1 }}>
                          {isMatched ? (
                            <CheckCircleIcon color="success" fontSize="small" />
                          ) : (
                            <CheckCircleOutlineIcon
                              color="inherit"
                              fontSize="small"
                            />
                          )}
                          {condition.label}
                        </ListItem>
                      );
                    })}
                  </List>
                </Typography>
              </Popover>

              <Typography
                style={{
                  fontSize: "14px",
                  fontWeight: 350,
                  lineHeight: "16px",
                  opacity: 0.5,
                }}
              >
                Follow the criteria while setting the password
              </Typography>
            </div>

            {/* Static Section */}
            <div className="terms-container">
              <Checkbox
                size="small"
                checked={isCheckboxChecked}
                onChange={(e) => setIsCheckboxChecked(e.target.checked)}
              />
              <Typography className="terms-text">
                I Accept to the{" "}
                <Link
                  component="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsPrivacyPolicyModalOpen(true);
                  }}
                >
                  Privacy Policy
                </Link>{" "}
                and{" "}
                <Link
                  component="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsTermsAndConditionsModalOpen(true);
                  }}
                >
                  Terms & Conditions
                </Link>
              </Typography>
            </div>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              disabled={
                !formik.isValid ||
                !formik.dirty ||
                !isCheckboxChecked ||
                loading ||
                isButtonDisabled
              }
              className="btn btn-primary"
              endIcon={
                loading ? <CircularProgress size={15} color="inherit" /> : null
              }
              type="submit"
            >
              Sign Up
            </Button>
            <div className="text_center text-muted">
              Already a member?{" "}
              <Link href="/login" className="singUp-link">
                Sign In
              </Link>
            </div>
          </form>
        </div>
      )}
      <PrivacyPolicy
        isPrivacyPolicyModalOpen={isPrivacyPolicyModalOpen}
        setIsPrivacyPolicyModalOpen={setIsPrivacyPolicyModalOpen}
      />
      <TermsConditions
        isTermsAndConditionsModalOpen={isTermsAndConditionsModalOpen}
        setIsTermsAndConditionsModalOpen={setIsTermsAndConditionsModalOpen}
      />
    </>
  );
};

export default Register;